import { TechniqueBreakdown } from "../techniqueBreakdowns";

export const numberTheoryTutorials: Record<string, TechniqueBreakdown> = {
  "6f7a8b9c-0d1e-2f3a-4b5c-6d7e8f9a0b1c": {
    title: "Introduction to Prime Numbers",
    description:
      "Learn to identify prime numbers using divisibility rules and the square root trick—the building blocks of all whole numbers!",
    steps: [
      {
        stepNumber: 1,
        title: "Strategy Identification",
        explanation:
          'When you see "Is [number] prime or composite?", think: "I\'ll test this number with my Divisibility Rules from Level 6, then use the Square Root Trick if needed."',
        tips: [
          'Prime = "lonely" number (only divisible by 1 and itself)',
          "Composite = can be broken into smaller factors",
          "Use the Two-Step Test: Easy Bosses → Final Boss",
          "The only even prime is 2!",
        ],
        examples: {
          simple: {
            problem: "Is 7 prime or composite?",
            solution: "Prime",
            explanation:
              "7 can only be made as 1 × 7. It has no other factors, so it's Prime.",
          },
          intermediate: {
            problem: "Is 6 prime or composite?",
            solution: "Composite",
            explanation:
              "6 can be made as 2 × 3. Since it has factors other than 1 and itself, it's Composite.",
          },
        },
      },
      {
        stepNumber: 2,
        title: "Step 1: The Easy Bosses",
        explanation:
          "Before doing hard math, use your quick Divisibility Rules! Check: (1) Is it even and not 2? (2) Does it end in 5 and not 5? (3) Do its digits add to a multiple of 3 and it's not 3?",
        tips: [
          "Even numbers (except 2) are always Composite",
          "Numbers ending in 5 (except 5) are divisible by 5",
          "Add the digits. If sum ÷ 3 works, the number is divisible by 3",
          "If ANY of these tests catch it, you're DONE! It's Composite",
        ],
        practice: {
          problem: "Is the number 83 prime or composite?",
          question: "Apply the Easy Bosses tests to 83. Fill in your findings:",
          answer: "pass",
          hint: "Check: Is 83 even? Does it end in 5? Calculate 8+3 and check if divisible by 3.",
          intermediateSteps: [
            { label: "Is 83 even?", answer: "no", hint: "It ends in 3" },
            { label: "Does it end in 5?", answer: "no", hint: "It ends in 3" },
            { label: "What is 8+3?", answer: "11", hint: "Add the digits" },
            {
              label: "Is 11 divisible by 3?",
              answer: "no",
              hint: "11 ÷ 3 has a remainder",
            },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Step 2: The Final Boss (Square Root Trick)",
        explanation:
          "If the number passes all Easy Bosses, use the Olympiad Trick: you only need to check prime factors up to its square root! Why? If N has factors, at least one must be ≤ √N.",
        tips: [
          'Find √N (you don\'t need the exact value, just "a bit more than X")',
          "List primes up to that square root: 2, 3, 5, 7, 11, 13...",
          "You already checked 2, 3, 5 in Easy Bosses!",
          "Test remaining primes by division. If any divide evenly, it's Composite",
        ],
        practice: {
          problem: "Is the number 83 prime or composite?",
          question:
            "Apply the Final Boss test. We need to check primes up to √83. Fill in:",
          answer: "prime",
          hint: "√83 is a bit over 9, so check prime 7 (already checked 2, 3, 5).",
          intermediateSteps: [
            {
              label: "What is √83 about?",
              answer: "9",
              hint: "9 × 9 = 81, so √83 ≈ 9.1",
            },
            {
              label: "Primes to check?",
              answer: "7",
              hint: "Already checked 2,3,5. Only 7 left under 9",
            },
            {
              label: "What is 83 ÷ 7?",
              answer: "11 r6",
              hint: "7×11=77, so 83-77=6",
            },
            {
              label: "Is 83 prime?",
              answer: "yes",
              hint: "Not divisible by any prime ≤ √83",
            },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Conclusion: Determine Prime or Composite",
        explanation:
          "After testing, make your conclusion. If it passed all Easy Bosses AND all Final Boss tests, it's Prime. If it failed ANY test (divisible by 2, 3, 5, 7, etc.), it's Composite.",
        tips: [
          "Prime = passed ALL tests (lonely number!)",
          "Composite = failed at least ONE test (has factors)",
          "The Square Root Trick saves you from testing hundreds of numbers!",
          "For 83: Not divisible by 2, 3, 5, or 7 → PRIME!",
        ],
        examples: {
          simple: {
            problem: "Is 51 prime or composite?",
            solution: "Composite",
            explanation:
              "Easy Boss test: 5+1=6, which IS divisible by 3! So 51 is Composite (51 = 3 × 17). We didn't even need the Final Boss!",
          },
          intermediate: {
            problem: "Is 97 prime or composite?",
            solution: "Prime",
            explanation:
              "Easy Bosses: Not even, doesn't end in 5, digits 9+7=16 (not ÷3). Final Boss: √97 ≈ 9.8, check 7. 97÷7=13 R6. Not divisible! 97 is Prime.",
          },
        },
      },
    ],
    finalExample: {
      problem: "Is 83 prime or composite?",
      fullSolution:
        "Prime! (Easy Bosses: Not even, doesn't end in 5, 8+3=11 not divisible by 3. Final Boss: √83 ≈ 9.1, check 7. 83÷7=11 R6. No factors found. 83 is Prime!)",
    },
  },

  "7a8b9c0d-1e2f-3a4b-5c6d-7e8f9a0b1c2e": {
    title: "Prime Factorization (The Factor Tree)",
    description:
      'Find the secret "prime recipe" hidden inside any whole number using the visual factor tree method.',
    steps: [
      {
        stepNumber: 1,
        title: "Start with Your Number",
        explanation:
          'Write the number at the top of your page. This is the "trunk" of your factor tree. For example, if you want to factorize 60, write 60 at the top.',
        tips: [
          "Choose a number to break down (e.g., 60)",
          "This number is composite (not prime) if you can factor it",
          "Write it clearly at the top - this is your starting point",
        ],
        examples: {
          simple: {
            problem: "Start factoring 12",
            solution: "12",
            explanation: "Write 12 at the top. This is your trunk.",
          },
          intermediate: {
            problem: "Start factoring 60",
            solution: "60",
            explanation: "Write 60 at the top of your factor tree.",
          },
        },
      },
      {
        stepNumber: 2,
        title: "Break It Into Two Factors",
        explanation:
          'Find ANY two numbers that multiply to make your number. Draw "branches" down to them. For 60, you could use 6×10, or 4×15, or 2×30 - any way works!',
        tips: [
          "Use your multiplication tables to find factors",
          "It doesn't matter which pair you choose - the final answer will be the same!",
          "Easier pairs: even numbers can split by 2, numbers ending in 5 split by 5",
          "Use divisibility rules to find quick factors",
        ],
        practice: {
          problem: "Break down 60 into two factors",
          question: "Choose any two numbers that multiply to 60:",
          answer: "6 × 10",
          hint: "Many answers work! 6×10, 4×15, 2×30, 3×20 all work. Let's use 6×10.",
          intermediateSteps: [
            { label: "First factor", answer: "6", hint: "6 is one factor" },
            {
              label: "Second factor",
              answer: "10",
              hint: "10 is the other factor",
            },
            {
              label: "Check",
              answer: "6 × 10 = 60",
              hint: "Verify they multiply to 60",
            },
          ],
        },
      },
      {
        stepNumber: 3,
        title: 'Identify Primes (The "Leaves")',
        explanation:
          'Look at your new numbers. Are they prime (can\'t be broken down further)? If prime, they\'re "leaves" - circle them! If composite, they\'re still "branches" that need breaking.',
        tips: [
          'Prime numbers are the "leaves" - you can\'t break them down further',
          "Small primes to remember: 2, 3, 5, 7, 11, 13, 17, 19",
          "If it's composite (like 6, 9, 10), keep breaking!",
          'Use the "Is it Prime?" technique from the last lesson',
        ],
        practice: {
          problem: "From our 6 and 10, which are prime?",
          question: "Check if 6 and 10 are prime or need more breaking:",
          answer: "both composite",
          hint: "6 = 2×3 and 10 = 2×5, so both need more breaking.",
          intermediateSteps: [
            {
              label: "Is 6 prime?",
              answer: "no",
              hint: "6 = 2 × 3, so it's composite",
            },
            {
              label: "Is 10 prime?",
              answer: "no",
              hint: "10 = 2 × 5, so it's composite",
            },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Keep Breaking the Branches",
        explanation:
          "Continue breaking down any composite number until all you have left are prime numbers (leaves). Break 6 into 2×3, break 10 into 2×5.",
        tips: [
          "Each branch keeps splitting until you hit primes",
          "Draw it like a tree with branches going down",
          "Stop when you can't break it down anymore (prime!)",
          "All the bottom numbers (leaves) should be prime",
        ],
        practice: {
          problem: "Continue breaking 6 and 10 from 60 = 6 × 10",
          question: "Factor the branches completely:",
          answer: "2, 3, 2, 5",
          hint: "6 breaks into 2×3. 10 breaks into 2×5.",
          intermediateSteps: [
            {
              label: "Break 6",
              answer: "2 × 3",
              hint: "6 = 2 × 3 (both prime!)",
            },
            {
              label: "Break 10",
              answer: "2 × 5",
              hint: "10 = 2 × 5 (both prime!)",
            },
            {
              label: "All leaves",
              answer: "2, 3, 2, 5",
              hint: "All four are prime - done!",
            },
          ],
        },
      },
      {
        stepNumber: 5,
        title: 'Write the Final "Prime Recipe"',
        explanation:
          "List all the prime leaves from smallest to largest. Use exponents to tidy it up: if you have two 2s, write 2². This is the unique prime factorization!",
        tips: [
          'Collect all the prime "leaves" from your tree',
          "Sort them: smallest to largest",
          "Count duplicates and use exponents: 2×2 = 2², 3×3×3 = 3³",
          'This is the "DNA" or "recipe" for your number',
        ],
        practice: {
          problem: "Write the prime recipe for 60 from leaves: 2, 3, 2, 5",
          question: "Organize and write with exponents:",
          answer: "2² × 3 × 5",
          hint: "Sort: 2, 2, 3, 5. Two 2s = 2². One 3 and one 5.",
          intermediateSteps: [
            {
              label: "Sort primes",
              answer: "2, 2, 3, 5",
              hint: "Smallest to largest",
            },
            { label: "Count 2s", answer: "2²", hint: "Two 2s → 2²" },
            { label: "Count others", answer: "3¹ × 5¹", hint: "One 3, one 5" },
            {
              label: "Final recipe",
              answer: "2² × 3 × 5",
              hint: "60 = 2² × 3 × 5",
            },
          ],
        },
      },
    ],
    finalExample: {
      problem: "Find the prime factorization of 60 using a factor tree",
      fullSolution:
        "2² × 3 × 5 (Tree: 60 = 6×10 → 6=2×3, 10=2×5 → Leaves: 2,2,3,5 → Recipe: 2² × 3 × 5)",
    },
  },

  "8b9c0d1e-2f3a-4b5c-6d7e-8f9a0b1c2d3f": {
    title: "Greatest Common Divisor (GCD/GCF)",
    description:
      "Find the biggest number that divides perfectly into two numbers using the prime recipe method.",
    steps: [
      {
        stepNumber: 1,
        title: "Get the Prime Recipe for Both Numbers",
        explanation:
          'Use factor trees to find the prime factorization of each number. This gives you the "ingredients" in each number.',
        tips: [
          "Use the Factor Tree method from the last lesson",
          "Write each number as a product of primes with exponents",
          "Example: 54 = 2 × 3³, not just 2 × 3 × 3 × 3",
          "Write them clearly - you'll compare them next",
        ],
        practice: {
          problem: "Find GCD(54, 72). First, get prime recipes.",
          question: "Factor both numbers:",
          answer: "54 = 2 × 3³, 72 = 2³ × 3²",
          hint: "54: 2×27 → 2×3×9 → 2×3×3×3 = 2¹×3³. 72: 8×9 → 2³×3²",
          intermediateSteps: [
            {
              label: "54 factored",
              answer: "2 × 3³",
              hint: "54 = 2 × 3 × 3 × 3 = 2¹ × 3³",
            },
            {
              label: "72 factored",
              answer: "2³ × 3²",
              hint: "72 = 8 × 9 = 2³ × 3²",
            },
          ],
        },
      },
      {
        stepNumber: 2,
        title: "Compare the Recipes",
        explanation:
          "Write the prime factorizations on top of each other. Look at each prime that appears. This helps you see what they share.",
        tips: [
          "Line up the recipes vertically",
          "Look at each prime separately (2s, then 3s, then 5s, etc.)",
          'For GCD, you want the "minimum shared ingredients"',
          "If a prime is missing from one, they share 0 of that prime",
        ],
        examples: {
          simple: {
            problem: "Compare: 12 = 2² × 3 and 18 = 2 × 3²",
            solution: "Shared: 2s and 3s",
            explanation:
              "Both have 2s (min of 2¹ and 2¹ is 2¹). Both have 3s (min of 3¹ and 3² is 3¹).",
          },
          intermediate: {
            problem: "Compare: 54 = 2¹ × 3³ and 72 = 2³ × 3²",
            solution: "Shared: 2s and 3s",
            explanation:
              "54 has one 2, 72 has three 2s. 54 has three 3s, 72 has two 3s.",
          },
        },
      },
      {
        stepNumber: 3,
        title: "Find the Minimum of Each Shared Prime",
        explanation:
          "For each prime that appears in BOTH recipes, take the SMALLER exponent. This gives you the biggest shared factor.",
        tips: [
          "For each prime, ask: which number has fewer of this prime?",
          "Take the smaller exponent from the two",
          "Twos: min(1, 3) = 1 means use 2¹",
          "If one recipe has 0 of a prime, the GCD has 0 of it too",
        ],
        practice: {
          problem: "Find minimums for GCD(54, 72). 54 = 2¹×3³, 72 = 2³×3²",
          question: "Take the minimum exponent for each prime:",
          answer: "2¹ × 3²",
          hint: "Twos: min(1,3)=1 → 2¹. Threes: min(3,2)=2 → 3²",
          intermediateSteps: [
            { label: "Min for 2s", answer: "2¹", hint: "min(2¹, 2³) = 2¹" },
            { label: "Min for 3s", answer: "3²", hint: "min(3³, 3²) = 3²" },
            {
              label: "GCD recipe",
              answer: "2¹ × 3²",
              hint: "Combine the minimums",
            },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Multiply to Get the GCD",
        explanation:
          "Multiply all the minimum prime powers together. This gives you the Greatest Common Divisor!",
        tips: [
          "Calculate the product of all the minimum primes",
          "2¹ × 3² = 2 × 9 = 18",
          "This is the largest number that divides both original numbers",
          "Use for simplifying fractions: divide numerator and denominator by GCD",
        ],
        practice: {
          problem: "Calculate GCD from recipe 2¹ × 3²",
          question: "Multiply to get the final answer:",
          answer: "18",
          hint: "2¹ = 2, 3² = 9. Then 2 × 9 = 18",
          intermediateSteps: [
            { label: "Calculate 2¹", answer: "2", hint: "2¹ = 2" },
            { label: "Calculate 3²", answer: "9", hint: "3² = 9" },
            { label: "Multiply", answer: "18", hint: "2 × 9 = 18" },
            {
              label: "GCD(54, 72)",
              answer: "18",
              hint: "The greatest common divisor is 18!",
            },
          ],
        },
      },
    ],
    finalExample: {
      problem: "Find GCD(54, 72) and use it to simplify 54/72",
      fullSolution:
        "GCD = 18. (54 = 2×3³, 72 = 2³×3². Minimums: 2¹×3² = 18) Simplified: 54÷18 = 3, 72÷18 = 4. Answer: 3/4",
    },
  },

  "9c0d1e2f-3a4b-5c6d-7e8f-9a0b1c2d3e4f": {
    title: "Least Common Multiple (LCM)",
    description:
      "Find the smallest number that both numbers divide into using the maximum prime powers.",
    steps: [
      {
        stepNumber: 1,
        title: "Get the Prime Recipe for Both Numbers",
        explanation:
          "Just like with GCD, start by finding the prime factorization of both numbers using factor trees.",
        tips: [
          "Factor each number completely into primes",
          "Write with exponents for clarity",
          "Example: 12 = 2² × 3, 18 = 2 × 3²",
          "This is the same first step as GCD!",
        ],
        examples: {
          simple: {
            problem: "Find LCM(6, 8). First get recipes.",
            solution: "6 = 2 × 3, 8 = 2³",
            explanation: "6 factors to 2×3. 8 factors to 2×2×2 = 2³.",
          },
          intermediate: {
            problem: "Find LCM(12, 18). Get prime recipes.",
            solution: "12 = 2² × 3, 18 = 2 × 3²",
            explanation: "12 = 4×3 = 2²×3. 18 = 2×9 = 2×3².",
          },
        },
      },
      {
        stepNumber: 2,
        title: "Compare and Identify All Primes",
        explanation:
          "Look at the prime recipes side by side. For LCM, you need EVERY prime that appears in EITHER recipe (not just shared ones like GCD).",
        tips: [
          "Write both recipes on top of each other",
          "List ALL primes that appear in either number",
          'For LCM, you\'re building a number that "contains" both originals',
          "Even if a prime only appears in one recipe, you need it!",
        ],
        practice: {
          problem: "Find LCM(12, 18). Compare: 12 = 2² × 3¹, 18 = 2¹ × 3²",
          question: "List all primes that appear:",
          answer: "2 and 3",
          hint: "Both recipes have 2s and 3s. Those are all the primes we need.",
          intermediateSteps: [
            { label: "Primes in 12", answer: "2 and 3", hint: "12 = 2² × 3" },
            { label: "Primes in 18", answer: "2 and 3", hint: "18 = 2 × 3²" },
            {
              label: "All primes",
              answer: "2 and 3",
              hint: "Union of both sets",
            },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Find the Maximum of Each Prime",
        explanation:
          'For each prime, take the LARGER exponent from the two recipes. This ensures the LCM contains enough of each prime to "cover" both numbers.',
        tips: [
          "Opposite of GCD! Now take the MAXIMUM, not minimum",
          "For each prime, which number needs more of it?",
          "Twos: max(2, 1) = 2 means use 2²",
          "The LCM must have enough of each prime for both numbers",
        ],
        practice: {
          problem: "Find maximums for LCM(12, 18). 12 = 2²×3¹, 18 = 2¹×3²",
          question: "Take the maximum exponent for each prime:",
          answer: "2² × 3²",
          hint: "Twos: max(2,1)=2 → 2². Threes: max(1,2)=2 → 3²",
          intermediateSteps: [
            { label: "Max for 2s", answer: "2²", hint: "max(2², 2¹) = 2²" },
            { label: "Max for 3s", answer: "3²", hint: "max(3¹, 3²) = 3²" },
            {
              label: "LCM recipe",
              answer: "2² × 3²",
              hint: "Combine the maximums",
            },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Multiply to Get the LCM",
        explanation:
          "Multiply all the maximum prime powers together. This is the Least Common Multiple - the smallest number both originals divide into!",
        tips: [
          "Calculate the product of all maximum primes",
          "2² × 3² = 4 × 9 = 36",
          'This is the smallest "common container" for both numbers',
          "Use for adding fractions: LCM is your common denominator!",
        ],
        practice: {
          problem: "Calculate LCM from recipe 2² × 3²",
          question: "Multiply to get the final answer:",
          answer: "36",
          hint: "2² = 4, 3² = 9. Then 4 × 9 = 36",
          intermediateSteps: [
            { label: "Calculate 2²", answer: "4", hint: "2² = 4" },
            { label: "Calculate 3²", answer: "9", hint: "3² = 9" },
            { label: "Multiply", answer: "36", hint: "4 × 9 = 36" },
            {
              label: "LCM(12, 18)",
              answer: "36",
              hint: "The least common multiple is 36!",
            },
          ],
        },
      },
    ],
    finalExample: {
      problem: "Find LCM(12, 18) and use it to add 1/12 + 1/18",
      fullSolution:
        "LCM = 36. (12 = 2²×3, 18 = 2×3². Maximums: 2²×3² = 36) Convert: 1/12 = 3/36, 1/18 = 2/36. Add: 3/36 + 2/36 = 5/36",
    },
  },

  "0d1e2f3a-4b5c-6d7e-8f9a-0b1c2d3e4f5a": {
    title: "How to Count Without Counting (The Multiplication Principle)",
    description:
      "Master the most important counting shortcut! Find the total number of possibilities by multiplying the number of choices for each separate step.",
    steps: [
      {
        stepNumber: 1,
        title: 'Identify the "Decisions" or "Slots"',
        explanation:
          'Read the problem carefully and figure out how many separate choices you need to make. Each choice is like a "slot" that needs to be filled. Draw a blank line for each decision to visualize the problem.',
        tips: [
          'Look for words like "and", "then", "followed by" - these indicate separate decisions',
          "Draw slots as blank lines: __ × __ × __",
          'For "outfits with shirts and pants", you have TWO slots: __(shirt)__ × __(pants)__',
          'For "ID with 1 letter and 2 digits", you have THREE slots: __(letter)__ × __(digit₁)__ × __(digit₂)__',
        ],
        practice: {
          problem:
            "A company creates ID codes with 1 letter (A-Z) followed by 2 digits (0-9). How many unique codes?",
          question:
            'How many separate "slots" or decisions do you need to make?',
          answer: "3",
          hint: "You need to choose: (1) one letter, (2) first digit, (3) second digit = 3 slots",
          intermediateSteps: [
            {
              label: "Letter choice",
              answer: "1 slot",
              hint: "Choose one letter from A-Z",
            },
            {
              label: "First digit",
              answer: "1 slot",
              hint: "Choose first digit (0-9)",
            },
            {
              label: "Second digit",
              answer: "1 slot",
              hint: "Choose second digit (0-9)",
            },
            {
              label: "Total slots",
              answer: "3",
              hint: "1 + 1 + 1 = 3 separate decisions",
            },
          ],
        },
      },
      {
        stepNumber: 2,
        title: "Count the Options for Each Slot",
        explanation:
          "For each slot you identified, count how many different options are available. Write the number above each slot. Be careful: some slots may have decreasing options if choices can't be repeated!",
        tips: [
          "Letters A-Z = 26 options",
          "Digits 0-9 = 10 options",
          "If choices CAN repeat: all slots have the same count",
          "If choices CANNOT repeat (like race positions): each slot decreases by 1",
          "Example with repeats allowed: _26_ × _10_ × _10_",
          "Example with no repeats (3 runners): _3_ × _2_ × _1_",
        ],
        practice: {
          problem:
            "For ID codes with 1 letter (A-Z) and 2 digits (0-9), fill in the options for each slot",
          question:
            "How many options for each slot? __(letter)__ × __(digit₁)__ × __(digit₂)__",
          answer: "26 × 10 × 10",
          hint: "26 letters, 10 digits each. Digits can repeat!",
          intermediateSteps: [
            {
              label: "Letter options",
              answer: "26",
              hint: "A through Z = 26 letters",
            },
            {
              label: "First digit options",
              answer: "10",
              hint: "0 through 9 = 10 digits",
            },
            {
              label: "Second digit options",
              answer: "10",
              hint: "0 through 9 = 10 digits (repeats OK!)",
            },
            {
              label: "Slots filled",
              answer: "26 × 10 × 10",
              hint: "Write the count above each slot",
            },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Multiply the Numbers",
        explanation:
          "The total number of unique combinations is the product of all the slot counts. Multiply all the numbers together to get your final answer. This works because each choice in one slot can be paired with EVERY choice in the other slots!",
        tips: [
          "Multiply left to right: first two numbers, then multiply by the next",
          "For 26 × 10 × 10: First 26 × 10 = 260, then 260 × 10 = 2,600",
          "Why multiply? Each of the 26 letters can pair with ALL 100 digit combinations (10 × 10)",
          "The Multiplication Principle: when you make one choice AND another, multiply the counts",
          "Compare to ADDITION: when you have one option OR another, you add instead",
        ],
        practice: {
          problem: "Calculate the total ID codes: 26 × 10 × 10",
          question: "What is the final product?",
          answer: "2600",
          hint: "First multiply 26 × 10, then multiply that result by 10",
          intermediateSteps: [
            {
              label: "26 × 10",
              answer: "260",
              hint: "Multiply the first two numbers",
            },
            {
              label: "260 × 10",
              answer: "2600",
              hint: "Multiply by the third number",
            },
            {
              label: "Total codes",
              answer: "2600",
              hint: "There are 2,600 unique ID codes possible!",
            },
          ],
        },
      },
      {
        stepNumber: 4,
        title: 'Critical Rule: "AND" vs "OR" (Multiply vs Add)',
        explanation:
          "The key to using the Multiplication Principle correctly is understanding WHEN to multiply and when to add. Use multiplication when you make one choice AND another choice (combined decisions). Use addition when you have one option OR another option (alternative choices).",
        tips: [
          'MULTIPLY (×): When decisions are COMBINED - "shirt AND pants", "letter AND digit"',
          'ADD (+): When choices are ALTERNATIVES - "large pizza OR medium pizza", "bus OR train"',
          "Example (AND): 3 shirts × 4 pants = 12 outfits (each shirt goes WITH each pant)",
          "Example (OR): 5 large + 3 medium pizzas = 8 total pizzas (can only pick one)",
          'Look for connecting words: "and/with/followed by" → multiply, "or/either" → add',
        ],
        practice: {
          problem:
            "A restaurant has 4 appetizers, 5 main courses, and 3 desserts. How many 3-course meals?",
          question: "Do you multiply or add? What is the answer?",
          answer: "60",
          hint: "You choose one appetizer AND one main AND one dessert. Multiply: 4 × 5 × 3",
          intermediateSteps: [
            {
              label: "Identify operation",
              answer: "Multiply",
              hint: "AND means multiply (all three courses together)",
            },
            {
              label: "Set up slots",
              answer: "4 × 5 × 3",
              hint: "4 appetizers, 5 mains, 3 desserts",
            },
            {
              label: "Calculate",
              answer: "60",
              hint: "4 × 5 = 20, then 20 × 3 = 60 meals",
            },
          ],
        },
      },
      {
        stepNumber: 5,
        title: "Special Case: No Repeats (Decreasing Choices)",
        explanation:
          'Sometimes choices cannot be reused once they\'re picked - like assigning race positions or seating arrangements. In these cases, each slot has fewer options than the one before because previous choices are "used up". This creates patterns like 3 × 2 × 1, which equals 3! (3 factorial).',
        tips: [
          "No repeats example: 3 runners in 1st/2nd/3rd → 3 × 2 × 1 = 6 ways",
          "First slot: 3 options (all runners available)",
          "Second slot: 2 options (one already placed in 1st)",
          "Third slot: 1 option (two already placed)",
          "This is the foundation of permutations (ordered arrangements)",
          "Contrast with repeats: if runners could tie, it would be 3 × 3 × 3 = 27",
        ],
        practice: {
          problem:
            "4 students compete for gold, silver, bronze medals. How many possible outcomes?",
          question: "Set up the slots with decreasing options and multiply:",
          answer: "24",
          hint: "No repeats! Each medal goes to a different student: 4 × 3 × 2",
          intermediateSteps: [
            {
              label: "Gold medal",
              answer: "4",
              hint: "All 4 students can win gold",
            },
            {
              label: "Silver medal",
              answer: "3",
              hint: "One already has gold, 3 left",
            },
            {
              label: "Bronze medal",
              answer: "2",
              hint: "Two have medals, 2 left",
            },
            {
              label: "Total outcomes",
              answer: "24",
              hint: "4 × 3 × 2 = 24 different outcomes",
            },
          ],
        },
      },
    ],
    finalExample: {
      problem:
        "How many 4-digit PIN codes (0-9) if digits CAN repeat vs CANNOT repeat?",
      fullSolution:
        "WITH repeats: 10 × 10 × 10 × 10 = 10,000 codes (each slot has all 10 digits). WITHOUT repeats: 10 × 9 × 8 × 7 = 5,040 codes (each slot loses one option). The difference shows why allowing repeats makes passwords stronger!",
    },
  },
};
